import { useEffect, useMemo, useRef, useState } from "react";
import { Link, useLocation, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { useUser } from "../auth/useUser";
import { Header } from "../components/Header";
import { useDocumentTitle } from "../hooks/useDocumentTitle";
import { getDisplayName } from "../lib/displayName";
import {
  refusalNoticeLine,
  withRefusal,
  type DraftRefusal,
  useDecisions,
  useQuestions,
  waitingNodeIds,
} from "../hooks/useQueues";
import { useMountGate } from "../hooks/useMountGate";
import { useAnswerQueue } from "../hooks/useAnswerQueue";
import {
  actMoveHoldOpen,
  actMoveRecord,
  endActMoveHold,
  markActMoveShown,
  openActMoveHold,
} from "../hooks/useSettleGuard";
import { QuestionPanel } from "../components/review/QuestionCard";
import { DecisionPanel } from "../components/review/DecisionCard";
import { ConfirmGroupCard, scrollPageToTop } from "../components/review/ConfirmGroupCard";
import { cardKeyAct, type HandOffState } from "../components/review/shared";
import { KEY_HINT_INLINE } from "../lib/platformKeys";
import { CONFIRM_GROUP_ID, reviewCards, waitingCardIds } from "../lib/reviewCards";
import { LIST_LOAD_ERROR_LINE } from "../lib/reviewLines";
import type { components } from "../api/types";

type Node = components["schemas"]["Node"];
type QueueKind = "questions" | "decisions";

// 0148 A31: the re-anchor's record of the card the founder was on. `pos` is
// the URL pos that card was resolved against and `rewrittenTo` the pos the
// re-anchor resolved it to (equal once the URL agrees). Keeping both lets a
// render that lands while the re-anchor's own URL rewrite is still pending
// (react-router applies location updates inside startTransition, while a
// query result or a keystroke renders urgently) read the OLD URL pos as "not
// moved" instead of as the founder's navigation.
export type ReviewAnchor = { queue: QueueKind; id: string; pos: number; rewrittenTo: number };

// anchorApplies is the ONE spelling of "the anchor still speaks for this
// render": it is for this queue and the URL has not moved since it was taken
// (the URL pos equals anchor.pos, or has only caught up with the anchor's own
// rewrite, anchor.rewrittenTo). A URL pos equal to neither is the founder's own
// navigation (goTo, goForward, a queue switch, Back/Forward, a pasted URL),
// which always wins. The re-anchor (resolveAnchoredPos) and the A40 hold (anchoredNodeLeft)
// both read it, so the two can never disagree about whether the founder moved.
export function anchorApplies(
  queue: QueueKind,
  urlPos: number,
  anchor?: ReviewAnchor,
): anchor is ReviewAnchor {
  return (
    anchor !== undefined &&
    anchor.queue === queue &&
    (urlPos === anchor.pos || urlPos === anchor.rewrittenTo)
  );
}

// resolveAnchoredPos keeps cards[pos] fixed under a founder when a queue
// refetch moves the DATA: when the anchor applies (above) and the card at the
// URL pos is no longer the anchored card, it returns the anchored card's new
// index while that card is still among the cards. In every other case it
// returns the URL pos: the founder's own navigation wins, and a departed
// anchored card falls back to the card that slid into its position (the clamp
// handles a queue that shrank past it).
export function resolveAnchoredPos(
  cardIds: readonly string[],
  queue: QueueKind,
  urlPos: number,
  anchor?: ReviewAnchor,
): number {
  if (anchorApplies(queue, urlPos, anchor) && cardIds[urlPos] !== anchor.id) {
    const moved = cardIds.indexOf(anchor.id);
    if (moved >= 0) return moved;
  }
  return urlPos;
}

// anchoredNodeLeft is true exactly when the anchor applies (above) and the
// anchored card is no longer among the cards - i.e. the panel about to render
// was mounted by a DATA swap, not by the founder (0148 A40: that panel holds
// its keystroke acts until a click inside it or a navigation).
export function anchoredNodeLeft(
  cardIds: readonly string[],
  queue: QueueKind,
  urlPos: number,
  anchor?: ReviewAnchor,
): boolean {
  return anchorApplies(queue, urlPos, anchor) && !cardIds.includes(anchor.id);
}

// 0051 S1c ("done" only when nothing waits; docs/todos/0051-frontend-views-refactor.md,
// slice 1): where a forward move lands. Skip, Save & next, a saved covered-by Yes or
// fork choice, Confirm, Reject, a playback's one-tap Yes, the shared card's last
// save, → and Cmd/Ctrl+→ all move through it. `cardIds`
// holds each queue's card ids and `waiting` each queue's waiting card ids
// (waitingCardIds over waitingNodeIds), null while that queue has no data. It
// finishes this tab, then the other tab, then comes back to what was skipped:
// the first waiting card of the active queue after `pos`; else the first
// waiting card of the other queue; else the first waiting card of the active
// queue before `pos` (one the founder skipped), never `pos` itself; else it
// returns null and the move leaves for the product page. Crossing before
// coming back keeps skip from cycling inside one tab. A queue with no data is
// never landed on and never makes the footer read "done".
export function forwardTarget(
  queue: QueueKind,
  pos: number,
  cardIds: Record<QueueKind, readonly string[]>,
  waiting: Record<QueueKind, ReadonlySet<string> | null>,
): { queue: QueueKind; pos: number } | null {
  const active = cardIds[queue];
  const activeWaiting = waiting[queue];
  if (activeWaiting !== null) {
    for (let i = pos + 1; i < active.length; i++) if (activeWaiting.has(active[i])) return { queue, pos: i };
  }
  const other: QueueKind = queue === "questions" ? "decisions" : "questions";
  const otherWaiting = waiting[other];
  if (otherWaiting !== null) {
    const i = cardIds[other].findIndex((id) => otherWaiting.has(id));
    if (i >= 0) return { queue: other, pos: i };
  }
  if (activeWaiting !== null) {
    for (let i = 0; i < Math.min(pos, active.length); i++) if (activeWaiting.has(active[i])) return { queue, pos: i };
  }
  return null;
}

// Focused-review surface. URL drives state: ?queue=questions&pos=N. A
// refresh-during-review preserves position; the pos is an index into the
// queue's cards (lib/reviewCards.ts) at fetch time. If the queue shrinks
// (drafts applied at next run) and pos goes out of range, we redirect to the
// last card.
export function Review() {
  const { productId } = useParams<{ productId: string }>();
  const [params, setParams] = useSearchParams();
  const navigate = useNavigate();
  const { data: user } = useUser();

  const queueKind = (params.get("queue") === "decisions" ? "decisions" : "questions") as QueueKind;
  const pos = Math.max(0, parseInt(params.get("pos") ?? "0", 10) || 0);

  // 0159 F2/F8: the refusal notice - the saves the server refused because the
  // item moved on (each a DraftRefusal: the code, the ruled line, the refused
  // item's id and title, and a covered-by Yes's holder title), shown above the
  // panel, one line each. It lives HERE, not in the panel, so it survives the
  // refused item leaving the queue (the queue refetch the refusal triggers),
  // and the panels hand addRefusal to their submit hooks' onRefused option, so
  // it lands even when the panel that posted has unmounted before the 409
  // arrived. It keeps every refusal, in the order they landed (withRefusal: a
  // newer refusal of the same item replaces the older), so a second refusal
  // never hides the first. Each line is worded by refusalNoticeLine at render.
  // Only the founder's own navigation clears the notice, every line at once,
  // read through anchorApplies (the effect below): the re-anchor's URL rewrite
  // and the clamp are data moves and leave it.
  const [refusals, setRefusals] = useState<DraftRefusal[]>([]);
  const addRefusal = (refusal: DraftRefusal) => setRefusals((list) => withRefusal(list, refusal));

  // 0074: the shared confirm card's answer queue (one per product for the
  // app's lifetime; its refusals reach this page's notice), and the item a link
  // named (the URL's fragment; "" when there is none or it is malformed).
  const answerQueue = useAnswerQueue(productId!, { onRefused: addRefusal });
  const fragment = useLocation().hash.replace(/^#/, "");
  let rowInView = "";
  try {
    rowInView = decodeURIComponent(fragment);
  } catch {
    rowInView = "";
  }

  const questions = useQuestions(productId!);
  const decisions = useDecisions(productId!);
  // 0051 S1a (F3-2): a queue served from cache at mount holds its acts until
  // its mount fetch settles (useMountGate); a refetch the founder's own save
  // starts never holds. Navigation is never held, and neither is the ordinary
  // question card's selection (AnswerOption, Edit, Write my own) and the shared
  // card's Edit; the authority fork and the covered-by row are held whole by
  // their disabled fieldset.
  const questionsHeld = useMountGate(questions, productId!);
  const decisionsHeld = useMountGate(decisions, productId!);
  const actsHeld = queueKind === "questions" ? questionsHeld : decisionsHeld;

  const activeQueue = queueKind === "questions" ? questions : decisions;
  const questionCards = useMemo(() => reviewCards(questions.data), [questions.data]);
  const decisionCards = useMemo(() => reviewCards(decisions.data), [decisions.data]);
  const cards = queueKind === "questions" ? questionCards : decisionCards;
  const items = activeQueue.data?.items ?? [];
  // 0148 A31 re-anchor: a queue refetch (a run end now invalidates both
  // queues) must not change the card under a founder who is typing - a
  // shifted cards[pos] would remount the panel (keyed on the card's id) and
  // move the caret. The anchor is recorded in an effect below; resolvedPos is
  // read wherever the URL pos used to be.
  const anchorRef = useRef<ReviewAnchor>();
  // The active queue's card ids, built once per render for the re-anchor, the
  // clamp and both panels' data-swap hold.
  const cardIds = cards.map((c) => c.id);
  const resolvedPos = resolveAnchoredPos(cardIds, queueKind, pos, anchorRef.current);
  const card = cards[resolvedPos];
  const node = card !== undefined && card.nodes.length === 1 ? card.nodes[0] : undefined;

  // 0051 S1c: an id->Node lookup over what the active queue serves - the
  // nodes its items name (its served context: the tree's copies of every ref
  // target, crossing decision, covered-by holder, reopened decision and the
  // root) and the items themselves, set last so an item's served copy wins -
  // so refs render with titles, never ids. Review reads no /tree: the queue
  // serves what the cards read, from the same read as its items.
  const context = activeQueue.data?.context;
  const nodeById = useMemo(() => {
    const m = new Map<string, Node>();
    for (const n of context?.nodes ?? []) m.set(n.id, n);
    for (const n of items) m.set(n.id, n);
    return m;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeQueue.data]);
  // The served meta: what the cards' lines read (where a question sits, the
  // "Settles:" labels), the product's name and the layers' status.
  const treeMeta = context?.meta;
  // 0051 S1b, S1c: whether the spec is handed off, from the root's served
  // copy (the queue's context always carries the root; a finalized spec's
  // marked card holds up nothing; no root served, as while the queue loads, is
  // "unknown", never "done").
  const rootStatus = nodeById.get("root")?.status;
  const handOff: HandOffState = rootStatus === undefined ? "unknown" : rootStatus === "confirmed" ? "done" : "open";
  // 0051 S1c: what waits on the founder in each queue (waitingNodeIds, the
  // one client read), null while that queue has no data - never read as
  // "nothing waits". The tabs' counts read it; every forward move reads the
  // cards it makes waiting (waitingCardIds).
  const waiting: Record<QueueKind, ReadonlySet<string> | null> = {
    questions: questions.data ? waitingNodeIds(questions.data) : null,
    decisions: decisions.data ? waitingNodeIds(decisions.data) : null,
  };
  const queueCardIds: Record<QueueKind, readonly string[]> = {
    questions: questionCards.map((c) => c.id),
    decisions: decisionCards.map((c) => c.id),
  };
  const waitingCards: Record<QueueKind, ReadonlySet<string> | null> = {
    questions: waiting.questions === null ? null : waitingCardIds(questionCards, waiting.questions),
    decisions: waiting.decisions === null ? null : waitingCardIds(decisionCards, waiting.decisions),
  };
  const nothingWaits =
    waiting.questions !== null &&
    waiting.decisions !== null &&
    waiting.questions.size === 0 &&
    waiting.decisions.size === 0;

  // Reset pos to 0 when switching queues.
  const setQueue = (k: QueueKind) => {
    const next = new URLSearchParams(params);
    next.set("queue", k);
    next.set("pos", "0");
    setParams(next, { replace: true });
  };

  // Move within the active queue: a backward move (← previous, Cmd/Ctrl+←).
  // Forward moves go through goForward ("done" only when nothing waits); the
  // exit below stays for an out-of-range call.
  const goTo = (newPos: number) => {
    if (newPos < 0) return;
    if (newPos >= cards.length) {
      navigate(`/products/${productId}`);
      return;
    }
    const next = new URLSearchParams(params);
    next.set("pos", String(newPos));
    setParams(next, { replace: false });
  };

  // 0051 S1c (the keyboard guard): the card the last forward move AN ACT
  // caused (Save & next, Confirm, Reject, the covered-by Yes, a saved fork
  // choice, a playback's one-tap Yes, the shared card's last save) showed, and
  // when it became the current one (`pending` until it renders), is the
  // act-move hold (hooks/useSettleGuard.ts), which this page writes. Its
  // keyboard acts wait ACT_SETTLE_MS from then, and Confirm and the covered-by
  // Yes take no click while the hold is open. A card the founder reached by
  // their own navigation (skip, →, Cmd/Ctrl+→, ← previous, a tab,
  // Back/Forward) is not held. No hold outlives the page.
  useEffect(() => () => endActMoveHold(), []);

  // 0051 S1c: every forward move - skip, Save & next, a saved Yes or fork
  // choice, Confirm, Reject, a playback's one-tap Yes, the shared card's last
  // save, → and Cmd/Ctrl+→ - lands where forwardTarget says: the rest of this
  // tab, then the other tab, then back to the ones skipped here; it leaves for
  // the product page when nothing else it can land on waits (a queue with no
  // data is never landed on).
  // The caller names the move's cause: `byAct` is an act's advance (the cards'
  // onActNext - Save & next, Confirm, Reject, the covered-by Yes, a saved fork
  // choice, a playback's one-tap Yes and the shared card's last save call it
  // from their success path), which opens the act-move hold on the card it
  // shows (its key acts, Confirm and the covered-by Yes wait ACT_SETTLE_MS);
  // the founder's own move (the cards' onNext - skip →, the right arrow - and
  // Cmd/Ctrl+→) opens none and ends any hold still open.
  const goForward = ({ byAct }: { byAct: boolean }) => {
    const target = forwardTarget(queueKind, resolvedPos, queueCardIds, waitingCards);
    if (target === null) {
      navigate(`/products/${productId}`);
      return;
    }
    if (byAct) openActMoveHold(queueCardIds[target.queue][target.pos]);
    else endActMoveHold();
    const next = new URLSearchParams(params);
    next.set("queue", target.queue);
    next.set("pos", String(target.pos));
    setParams(next, { replace: false });
  };

  // 0074: leaving the shared confirm card (its move-on, skip, previous, a tab,
  // a data move) scrolls the page to the top, so the next card's read starts in
  // view. Read on the card id's change, never on the card's unmount (StrictMode
  // runs a cleanup right after mount); equal ids never scroll.
  const prevCardId = useRef<string | undefined>(undefined);
  useEffect(() => {
    if (prevCardId.current === CONFIRM_GROUP_ID && card?.id !== CONFIRM_GROUP_ID) scrollPageToTop();
    prevCardId.current = card?.id;
  }, [card?.id]);

  // The act-move hold's clock starts when the card an act's move showed
  // becomes the current one; once the founder is on another card, the hold is
  // over (coming back to it later is not held).
  useEffect(() => {
    const shown = actMoveRecord();
    if (shown === null) return;
    if (card?.id === shown.id) {
      if (shown.pending) markActMoveShown(shown.id);
    } else if (!shown.pending) {
      endActMoveHold();
    }
  }, [card?.id]);

  // 0051 S1c (the keyboard guard): a card's key act (cardKeyAct, the cards'
  // own spelling in components/review/shared.tsx) does nothing when it is an
  // auto-repeat (a held key), always, or when it lands within ACT_SETTLE_MS
  // of an act's forward move showing the card on screen (or before that card
  // renders), so a key can only act on a card the founder has seen. It is
  // taken on the window's capture phase, before every card's own listener (the
  // authority fork's and the covered-by amend's included).
  useEffect(() => {
    const onKeyCapture = (e: KeyboardEvent) => {
      if (cardKeyAct(e) === null) return;
      const inWindow = actMoveHoldOpen(card?.id);
      if (!e.repeat && !inWindow) return;
      e.preventDefault();
      e.stopImmediatePropagation();
    };
    window.addEventListener("keydown", onKeyCapture, true);
    return () => window.removeEventListener("keydown", onKeyCapture, true);
  }, [card?.id]);

  // 0159 F2/F8: the refusal notice's ONE clear rule - the URL moved and the
  // anchor no longer applies, i.e. the founder's own navigation (goTo,
  // goForward, a queue switch, Back/Forward, a pasted URL), in anchorApplies'
  // own spelling. The re-anchor's rewrite and the clamp record the pos they
  // write as the anchor's rewrittenTo, so the render that catches up with
  // either still reads "not moved" and keeps the notice; a data swap does not
  // move the URL at all.
  // Declared before the clamp and the anchor-recording effect (the two effects
  // that write the anchor) so it reads the anchor as the previous render left
  // it.
  useEffect(() => {
    if (!anchorApplies(queueKind, pos, anchorRef.current)) {
      setRefusals((list) => (list.length === 0 ? list : []));
    }
  }, [queueKind, pos]);

  // Out-of-range pos -> clamp once on data arrival. Don't redirect on
  // empty queue; render the empty-state below instead. Reads resolvedPos: when
  // the URL pos is past the end but the anchored node is still present, the
  // re-anchor's rewrite (below) moves the URL, and a clamp from the same
  // params in the same flush would race it.
  useEffect(() => {
    if (!activeQueue.isLoading && cards.length > 0 && resolvedPos >= cards.length) {
      // 0148 A40 (the clamp path): the anchored node was the LAST item and
      // left. The clamp's own rewrite is not the founder's navigation, so
      // record it on the anchor; the panel the clamp mounts is then held like
      // any other data-swapped panel.
      const anchor = anchorRef.current;
      if (anchor !== undefined && anchoredNodeLeft(cardIds, queueKind, pos, anchor)) {
        anchorRef.current = { ...anchor, rewrittenTo: cards.length - 1 };
      }
      const next = new URLSearchParams(params);
      next.set("pos", String(cards.length - 1));
      setParams(next, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeQueue.isLoading, cards.length, resolvedPos]);

  // 0148 A31: record the anchor for the node this render resolved to, and when
  // the re-anchor moved it off the URL pos, rewrite the URL's pos to match
  // (replace: history holds no phantom entry, and the next render is stable).
  // `pos` is the URL pos this render resolved against, so anchor.pos takes the
  // new value only once the URL agrees.
  useEffect(() => {
    if (!card) return;
    anchorRef.current = { queue: queueKind, id: card.id, pos, rewrittenTo: resolvedPos };
    if (resolvedPos !== pos) {
      const next = new URLSearchParams(params);
      next.set("pos", String(resolvedPos));
      setParams(next, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [queueKind, card?.id, pos, resolvedPos]);

  // Global keyboard handlers that should work even when the textarea is
  // focused: Escape (always exits to product page) and Cmd/Ctrl+Arrow
  // (navigate without leaving textarea). Plain ←/→ navigation is handled
  // inside QuestionPanel/DecisionPanel and the shared confirm card and gated on
  // textarea focus so caret movement still works inside the answer field.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        e.preventDefault();
        navigate(`/products/${productId}`);
        return;
      }
      if ((e.metaKey || e.ctrlKey) && e.key === "ArrowRight") {
        e.preventDefault();
        goForward({ byAct: false });
        return;
      }
      if ((e.metaKey || e.ctrlKey) && e.key === "ArrowLeft") {
        e.preventDefault();
        if (resolvedPos > 0) goTo(resolvedPos - 1);
        return;
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [resolvedPos, cards.length, productId, queueKind, questions.data, decisions.data]);

  // 0051 S1c: the page chrome (the product's name in the breadcrumb and the
  // tab title) reads the served meta of whichever queue has loaded, the active
  // one first, so a failed or loading tab never leaves the header reading
  // "loading…" while the other queue serves the same meta.
  const chromeMeta = (activeQueue.data ?? questions.data ?? decisions.data)?.context.meta;
  const displayName = chromeMeta ? getDisplayName(chromeMeta) : null;
  useDocumentTitle(displayName);

  if (!user) return null;

  const ideaText = chromeMeta?.idea ?? "";
  // 0051 S1c: "loading…" only while a queue is still loading (it may yet serve
  // the name); once neither is and none served the name (both lists failed to
  // load), the breadcrumb names nothing - never "loading…" beside the line that
  // says the list didn't load.
  const namePreview = displayName
    ? truncate(displayName, 40)
    : questions.isLoading || decisions.isLoading
      ? "loading…"
      : null;

  const breadcrumb = (
    <>
      <span className="text-ink-600">/</span>
      <Link to="/" className="font-mono text-ink-400 hover:text-ink-200">
        ideas
      </Link>
      <span className="text-ink-600">/</span>
      {namePreview !== null && (
        <>
          <Link
            to={`/products/${productId}`}
            className="font-mono text-ink-300 hover:text-ink-100 truncate max-w-[28ch]"
            title={ideaText}
          >
            {namePreview}
          </Link>
          <span className="text-ink-600">/</span>
        </>
      )}
      <span className="font-mono text-ink-300">review</span>
    </>
  );

  return (
    <div className="min-h-screen flex flex-col">
      <Header user={user} breadcrumb={breadcrumb} />

      <div className="border-b border-ink-800 bg-ink-950/70">
        <div className="max-w-6xl mx-auto px-4 sm:px-8 h-12 flex items-center gap-6">
          <QueueTab
            label="Questions"
            count={waiting.questions?.size ?? null}
            active={queueKind === "questions"}
            onClick={() => setQueue("questions")}
            accent="text-accent"
          />
          <QueueTab
            label="Decisions"
            count={waiting.decisions?.size ?? null}
            active={queueKind === "decisions"}
            onClick={() => setQueue("decisions")}
            accent="text-warn"
          />
          <div className="ml-auto flex shrink-0 items-center gap-4 whitespace-nowrap font-mono text-[11px] text-ink-400">
            <Link data-testid="review-back" to={`/products/${productId}`} className="hover:text-ink-200">
              <span className={KEY_HINT_INLINE}>esc · </span>back
            </Link>
          </div>
        </div>
      </div>

      <main className="flex-1 max-w-6xl mx-auto w-full px-4 sm:px-8 py-12">
        {/* Review reads better at a focused text width (~3xl); outer
            max-w-6xl keeps the page chrome aligned with the rest of
            the app. */}
        <div className="max-w-3xl">
        {/* 0159 F2/F8: above the panel and on the empty-queue branch alike, so
            it stays while the refused item leaves and the next one slides in
            (held by A40 until a click inside it or a navigation); one line per
            refusal, each worded by refusalNoticeLine. A line is keyed by its
            item's id, so a refusal that lands adds (or moves) its own line and
            leaves the others' nodes in place. */}
        {refusals.length > 0 && (
          <p
            role="status"
            data-testid="review-refusal"
            className="mb-6 space-y-1 rounded-md border border-danger-solid/30 bg-danger-solid/10 px-4 py-3 text-sm text-danger-strong"
          >
            {refusals.map((r, i) => (
              <span
                key={r.nodeId !== "" ? `id:${r.nodeId}` : `at:${i}`}
                data-testid="review-refusal-line"
                className="block"
              >
                {refusalNoticeLine(r, node?.id)}
              </span>
            ))}
          </p>
        )}

        {activeQueue.isLoading && (
          <p className="text-sm text-ink-400 font-mono">loading queue…</p>
        )}

        {/* 0051 S1c (RULED 2026-10-07): the active tab's list failed to load and
            nothing is cached - say so, never an empty page or a false "caught up". */}
        {activeQueue.isError && activeQueue.data === undefined && (
          <p data-testid="review-load-error" className="text-sm text-ink-400">
            {LIST_LOAD_ERROR_LINE}
          </p>
        )}

        {/* 0051 S1c: the empty state only on a served empty queue - never while it loads or after a failed load. */}
        {activeQueue.data !== undefined && cards.length === 0 && (
          <EmptyQueue queueKind={queueKind} productId={productId!} nothingWaits={nothingWaits} />
        )}

        {card && node && queueKind === "questions" && (
          <QuestionPanel
            key={card.id}
            productId={productId!}
            node={node}
            nodeById={nodeById}
            pos={resolvedPos}
            nothingWaits={nothingWaits}
            onPrev={() => goTo(resolvedPos - 1)}
            onNext={() => goForward({ byAct: false })}
            onActNext={() => goForward({ byAct: true })}
            awaitsCoveredByRuling={questions.data?.coveredByRulingNodeIds.has(node.id) ?? false}
            hasSavedDraft={questions.data?.draftedNodeIds.has(node.id) ?? false}
            answerUnderReview={questions.data?.answersUnderReviewNodeIds.has(node.id) ?? false}
            blocksContinue={questions.data?.triggerBlockingNodeIds.has(node.id) ?? true}
            handOff={handOff}
            mountedByDataSwap={anchoredNodeLeft(cardIds, queueKind, pos, anchorRef.current)}
            onRefused={addRefusal}
            treeMeta={treeMeta}
            actsHeld={actsHeld}
          />
        )}

        {/* 0074: a pass's waiting questions whose only option is the confirm
            option share one card (lib/reviewCards.ts), keyed on its one id,
            so saves, refetches and data moves never remount it. */}
        {card && card.nodes.length > 1 && queueKind === "questions" && (
          <ConfirmGroupCard
            key={card.id}
            productId={productId!}
            nodes={card.nodes}
            served={questions.data}
            servedAt={questions.dataUpdatedAt}
            treeMeta={treeMeta}
            pos={resolvedPos}
            nothingWaits={nothingWaits}
            onPrev={() => goTo(resolvedPos - 1)}
            onNext={() => goForward({ byAct: false })}
            onActNext={() => goForward({ byAct: true })}
            actsHeld={actsHeld}
            mountedByDataSwap={anchoredNodeLeft(cardIds, queueKind, pos, anchorRef.current)}
            queue={answerQueue}
            rowInView={rowInView}
          />
        )}

        {card && node && queueKind === "decisions" && (
          <DecisionPanel
            key={card.id}
            productId={productId!}
            node={node}
            nodeById={nodeById}
            pos={resolvedPos}
            nothingWaits={nothingWaits}
            onPrev={() => goTo(resolvedPos - 1)}
            onNext={() => goForward({ byAct: false })}
            onActNext={() => goForward({ byAct: true })}
            replacedByNewAnswer={decisions.data?.replacedByNewAnswerNodeIds.has(node.id) ?? false}
            mountedByDataSwap={anchoredNodeLeft(cardIds, queueKind, pos, anchorRef.current)}
            onRefused={addRefusal}
            treeMeta={treeMeta}
            actsHeld={actsHeld}
            dependents={context?.dependents?.[node.id] ?? []}
          />
        )}
        </div>
      </main>
    </div>
  );
}

function QueueTab({
  label,
  count,
  active,
  onClick,
  accent,
}: {
  label: string;
  count: number | null;
  active: boolean;
  onClick: () => void;
  accent: string;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`relative h-12 flex items-center gap-2 font-mono text-xs uppercase tracking-widest transition-colors ${
        active ? "text-ink-100" : "text-ink-400 hover:text-ink-200"
      }`}
    >
      <span>{label}</span>
      {/* 0051 S1c: no number until the queue has loaded - never a false 0. */}
      {count !== null && <span className={count === 0 ? "text-ink-500" : accent}>{count}</span>}
      {active && <span className="absolute bottom-0 left-0 right-0 h-px bg-ink-100" />}
    </button>
  );
}

function EmptyQueue({
  queueKind,
  productId,
  nothingWaits,
}: {
  queueKind: QueueKind;
  productId: string;
  nothingWaits: boolean;
}) {
  const label = queueKind === "questions" ? "open questions" : "pending decisions";
  return (
    <div className="text-center py-24">
      <div className="inline-block h-1.5 w-1.5 rounded-full bg-ok mb-6" />
      <h2 className="text-2xl font-medium text-ink-100 tracking-tightest">No {label}</h2>
      {/* 0051 S1c: "caught up" only when nothing waits in either tab. */}
      {nothingWaits && (
        <p className="mt-2 text-sm text-ink-400 max-w-md mx-auto">
          You're caught up.
        </p>
      )}
      <Link
        to={`/products/${productId}`}
        className="mt-6 inline-block text-sm text-accent hover:text-accent-glow"
      >
        ← back to spec
      </Link>
    </div>
  );
}

function truncate(s: string, max: number): string {
  if (s.length <= max) return s;
  return s.slice(0, max - 1).trimEnd() + "…";
}
